import MainURL from '../../../MainURL';
import type { NoticeItem, PlayRecord } from '../data/constants';
import type { CognitiveActivity } from '../data/cognitive';
import type { Category, Song } from '../data/mockCatalog';
import type { DrumUserPreferences, DrumUserProfile } from '../store/drumAppAtoms';

async function readJson(response: Response) {
  return response.json().catch(() => null);
}

function authHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchMemberSession(token: string): Promise<{ user: DrumUserProfile; preferences: DrumUserPreferences }> {
  const response = await fetch(`${MainURL}/auth/me`, { headers: authHeaders(token) });
  const data = await readJson(response);
  if (!response.ok) {
    const error = new Error(data?.message || 'Unable to load profile.') as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return { user: data.user, preferences: data.preferences };
}

export async function saveMemberProfile(token: string, name: string): Promise<DrumUserProfile> {
  const response = await fetch(`${MainURL}/auth/me`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ name }),
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to save changes.');
  }
  return data.user;
}

export async function saveMemberPreferences(token: string, preferences: Partial<DrumUserPreferences>): Promise<DrumUserPreferences> {
  const response = await fetch(`${MainURL}/auth/preferences`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(preferences),
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to save changes.');
  }
  return data.preferences;
}

export async function fetchPublicNotices(): Promise<NoticeItem[]> {
  const response = await fetch(`${MainURL}/notices`);
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to load notices.');
  }
  return Array.isArray(data?.notices) ? data.notices : [];
}

export async function fetchPublicNotice(noticeId: string): Promise<NoticeItem> {
  const response = await fetch(`${MainURL}/notices/${noticeId}`);
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to load notices.');
  }
  return data.notice;
}

function resolveAssetUrl(path?: string | null) {
  if (!path) return '';
  if (/^(https?:|blob:|data:)/.test(path)) return path;
  return `${MainURL}${path}`;
}

function emptyDifficulties(): Song['difficulties'] {
  return {
    Easy: { code: 'easy', labelKo: '이지', labelEn: 'Easy', format: 'color-drum', abcKey: 'Easy', freeAccess: false },
    Normal: { code: 'normal', labelKo: '노멀', labelEn: 'Normal', format: 'color-staff', abcKey: 'Normal', freeAccess: false },
    Hard: { code: 'hard', labelKo: '하드', labelEn: 'Hard', format: 'classic-staff', abcKey: 'Hard', freeAccess: false },
  };
}

function toSong(raw: Partial<Song> & { songId?: string }): Song | null {
  if (!raw?.songId || !raw.title) return null;
  const audio = resolveAssetUrl(raw.playAudio || raw.previewAudio);
  return {
    songId: String(raw.songId),
    categoryId: String(raw.categoryId || ''),
    title: String(raw.title || ''),
    artist: String(raw.artist || ''),
    thumbnail: resolveAssetUrl(raw.thumbnail),
    previewAudio: audio,
    playAudio: audio,
    bpm: Number(raw.bpm) || 0,
    publishStatus: raw.publishStatus === 'private' ? 'private' : 'public',
    registeredAt: String(raw.registeredAt || ''),
    contentVersion: String(raw.contentVersion || ''),
    audioStartOffset: Number(raw.audioStartOffset) || 0,
    difficulties: raw.difficulties?.Easy && raw.difficulties?.Normal && raw.difficulties?.Hard
      ? raw.difficulties
      : emptyDifficulties(),
  };
}

export async function fetchPublicScores(): Promise<{ categories: Category[]; songs: Song[] }> {
  const response = await fetch(`${MainURL}/scores`);
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to load scores.');
  }
  const categories: Category[] = Array.isArray(data?.categories)
    ? data.categories.map((item: Category) => ({
        categoryId: String(item.categoryId),
        nameKo: item.nameKo || '',
        nameEn: item.nameEn || item.nameKo || '',
        order: Number(item.order) || 0,
      }))
    : [];
  const songs = Array.isArray(data?.songs)
    ? data.songs.map((item: Partial<Song>) => toSong(item)).filter((song: Song | null): song is Song => Boolean(song))
    : [];
  return { categories, songs };
}

function toCognitiveActivity(raw: Partial<CognitiveActivity> & { id?: string }): CognitiveActivity | null {
  if (!raw?.id) return null;
  return {
    id: String(raw.id),
    titleKo: String(raw.titleKo || raw.titleEn || ''),
    titleEn: String(raw.titleEn || raw.titleKo || ''),
    descriptionKo: String(raw.descriptionKo || raw.descriptionEn || ''),
    descriptionEn: String(raw.descriptionEn || raw.descriptionKo || ''),
    categoryKo: String(raw.categoryKo || ''),
    categoryEn: String(raw.categoryEn || raw.categoryKo || ''),
    thumbnail: resolveAssetUrl(raw.thumbnail),
    stages: Number(raw.stages) || 0,
    playable: Boolean(raw.playable),
    freeAccess: raw.freeAccess !== false,
    playCode: raw.playCode || '',
  };
}

export async function fetchPublicCognitiveActivities(): Promise<CognitiveActivity[]> {
  const response = await fetch(`${MainURL}/cognitive`);
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to load activities.');
  }
  return Array.isArray(data?.activities)
    ? data.activities.map((item: Partial<CognitiveActivity>) => toCognitiveActivity(item)).filter((item: CognitiveActivity | null): item is CognitiveActivity => Boolean(item))
    : [];
}

function toPlayRecord(raw: Partial<PlayRecord> & { playId?: string }): PlayRecord | null {
  if (!raw?.playId) return null;
  return {
    playId: String(raw.playId),
    scoreId: raw.scoreId ? String(raw.scoreId) : '',
    title: String(raw.title || ''),
    difficulty: String(raw.difficulty || ''),
    score: Number(raw.score) || 0,
    rank: String(raw.rank || ''),
    accuracy: Number(raw.accuracy) || 0,
    maxCombo: Number(raw.maxCombo) || 0,
    playedAt: String(raw.playedAt || ''),
    durationMs: Number(raw.durationMs) || 0,
    stats: raw.stats,
    mistakes: raw.mistakes,
    previousScore: raw.previousScore,
  };
}

export async function fetchPlayRecords(token: string): Promise<PlayRecord[]> {
  const response = await fetch(`${MainURL}/plays`, { headers: authHeaders(token) });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to load records.');
  }
  return Array.isArray(data?.records)
    ? data.records.map((item: Partial<PlayRecord>) => toPlayRecord(item)).filter((item: PlayRecord | null): item is PlayRecord => Boolean(item))
    : [];
}

export async function fetchFavorites(token: string, type: 'score' | 'cognitive'): Promise<string[]> {
  const response = await fetch(`${MainURL}/favorites?type=${encodeURIComponent(type)}`, { headers: authHeaders(token) });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to load favorites.');
  }
  return Array.isArray(data?.ids) ? data.ids.map((id: unknown) => String(id)) : [];
}

export async function toggleFavorite(token: string, type: 'score' | 'cognitive', id: string): Promise<{ ids: string[]; liked: boolean }> {
  const response = await fetch(`${MainURL}/favorites`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ type, id }),
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to save favorite.');
  }
  const ids = Array.isArray(data?.ids) ? data.ids.map((item: unknown) => String(item)) : [];
  return { ids, liked: Boolean(data?.liked) };
}

export async function savePlayRecord(token: string, payload: {
  scoreId: number;
  title?: string;
  difficulty: string;
  score: number;
  accuracy: number;
  maxCombo: number;
  rank: string;
  durationMs?: number;
  stats?: PlayRecord['stats'];
  mistakes?: PlayRecord['mistakes'];
}): Promise<PlayRecord> {
  const response = await fetch(`${MainURL}/plays`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data?.message || 'Unable to save record.');
  }
  const record = toPlayRecord(data?.record);
  if (!record) throw new Error(data?.message || 'Unable to save record.');
  return record;
}
